const d3 = require('d3');
import { getParent } from '../utilities';

export default function (chartEl, boxes, prices, activeTimePeriod) {
  let minDate, maxDate, maxTransactionValue, boxesByQuarter;
  const boxesContainer = d3
    .select(chartEl)
    .append('div')
    .attr('class', 'activitiy-chart__container');

  init();

  function init() {
    boxesByQuarter = groupBoxesByQuarter(boxes);
    maxTransactionValue = maxValue(boxesByQuarter);
    renderChart();
  }

  function renderChart() {
    minDate = prices[0].date;
    maxDate = prices[prices.length - 1].date;
    const times = quartersBetween(minDate, maxDate);
    let quarter = getQuarter(minDate);
    let year = minDate.getUTCFullYear();

    for (let i = 0; i < times; i++) {
      const box = boxesByQuarter[quarter + '-' + year];
      const style = firstAndLastBoxStyle(i, times, minDate, maxDate);
      createBox(box, style, quarter, year);

      if (quarter >= 4) {
        quarter = 1;
        year += 1;
      } else {
        quarter += 1;
      }
    }
  }

  function resize(newPrices, _newYAxisLimits, newActiveTimePeriod) {
    prices = newPrices;
    activeTimePeriod = newActiveTimePeriod;
    boxesContainer.html('');
    renderChart();
  }

  function createBox(box, style, quarter, year) {
    const container = boxesContainer.append('div')
      .attr('class', 'activitiy-chart__boxes')
      .attr('style', style);

    if (box && (box.buyValue || box.sellValue)) {
      container.attr('data-quarter', 'Q' + quarter + ' ' + year)
        .attr('data-buys', box.buys)
        .attr('data-buy-value', box.buyValue)
        .attr('data-sells', box.sells)
        .attr('data-sell-value', box.sellValue)
        .attr('data-buy-gurus', JSON.stringify(box.buyGurus))
        .attr('data-sell-gurus', JSON.stringify(box.sellGurus));

      container.append('div').attr('class', 'activitiy-chart__box--buy')
        .attr('style', 'height: ' + box.buyValue / maxTransactionValue * 100 / 2 + '%');
      container.append('div').attr('class', 'activitiy-chart__box--sell')
        .attr('style', 'height: ' + box.sellValue / maxTransactionValue * 100 / 2 + '%');

      container.node().addEventListener('mouseover', showTooltip);
    }
  }

  return {
    resize: resize,
  };
}

function groupBoxesByQuarter(boxes) {
  const r = {};

  for (let box of boxes) {
    const key = getKey(box.start_date)
    r[key] ||= { buyValue: 0, buys: 0, sellValue: 0, sells: 0, totalValue: 0, buyGurus: [], sellGurus: [], boxes: [] };
    r[key].totalValue += box.share_change * box.price;
    r[key].boxes.push(box);

    if (box.type === 'buy') {
      r[key].buys += 1;
      r[key].buyValue += box.share_change * box.price;
      r[key].buyGurus.push(box.guru);
    } else {
      r[key].sells += 1;
      r[key].sellValue -= box.share_change * box.price;
      r[key].sellGurus.push(box.guru);
    }
  }

  return r;
}

function getKey(date) {
  const quarter = getQuarter(date);
  const year = date.getUTCFullYear();
  return quarter + '-' + year;
}

function getQuarter(date) {
  date = date || new Date();
  const m = Math.floor(date.getUTCMonth() / 3) + 1;
  return m > 4 ? m - 4 : m;
}

function quartersBetween(startDate, endDate) {
  const yearsBetween = endDate.getUTCFullYear() - startDate.getUTCFullYear();
  const quartersBetween = getQuarter(endDate) - getQuarter(startDate) + 1;
  return yearsBetween * 4 + quartersBetween;
}

function firstAndLastBoxStyle(i, times, startDate, endDate) {
  let style;
  if (i === 0) {
    style = 'flex: ' + (1 - quarterProgress(startDate));
  } else if (i === times - 1) {
    style = 'flex: ' + quarterProgress(endDate);
  }
  return style;
}

function quarterProgress(date) {
  return (date.getUTCMonth() % 3 / 3) + (date.getUTCDate() / 30 / 3);
}

function maxValue(obj) {
  let max = 1;

  Object.keys(obj).forEach(key => {
    const value = Math.max(obj[key]['sellValue'], obj[key]['buyValue']);
    if (value > max) {
      max = value;
    }
  });

  return max;
}

function showTooltip(event) {
  const el = getParent(event.target, 'activitiy-chart__boxes');
  el.removeEventListener('mouseover', showTooltip);

  const container = document.createElement('div');

  const header = document.createElement('header');
  let text = document.createTextNode(el.dataset.quarter);
  header.appendChild(text);

  const buys = document.createElement(el.dataset.buys === '0' ? 'p' : 'button');
  buys.dataset.type = 'buy';
  if (el.dataset.buys === '0') {
    buys.classList.add('activitiy-chart__tooltip-text--muted');
  } else {
    buys.addEventListener('click', showGurusInTooltip);
  }
  text = document.createTextNode(transactionText(el.dataset.buyValue, el.dataset.buys, 'bought'));
  buys.appendChild(text);

  const sells = document.createElement(el.dataset.sells === '0' ? 'p' : 'button');
  sells.dataset.type = 'sell';
  if (el.dataset.sells === '0') {
    sells.classList.add('activitiy-chart__tooltip-text--muted');
  } else {
    sells.addEventListener('click', showGurusInTooltip);
  }
  text = document.createTextNode(transactionText(el.dataset.sellValue, el.dataset.sells, 'sold'));
  sells.appendChild(text);

  container.appendChild(header);
  container.appendChild(buys);
  container.appendChild(sells);
  container.classList.add('activitiy-chart__tooltip');
  el.appendChild(container);
  if (container.getBoundingClientRect().right > window.innerWidth) {
    container.classList.add('activitiy-chart__tooltip--right');
  }
  el.addEventListener('mouseleave', hideTooltip);
}

function hideTooltip(event) {
  const el = getParent(event.target, 'activitiy-chart__boxes');
  el.removeEventListener('mouseleave', hideTooltip);
  el.querySelector('div:last-child').remove();
  el.addEventListener('mouseover', showTooltip);
}

function transactionText(value, count, type) {
  if (count === '0') {
    return 'No investor ' + type;
  }

  return '$' + humanize(value) + ' ' + type + ' by ' + pluralize('investor', count);
}

function humanize(value) {
  if (value > 1_000_000_000) {
    return Math.round(value / 100_000_000) / 10 + ' billion';
  } else if (value > 1_000_000) {
    return Math.round(value / 100_000) / 10 + ' million';
  } else if (value > 10_000) {
    return Math.round(value / 1_000) + 'k';
  } else {
    return value;
  }
}

function pluralize(text, count) {
  if (count === '1') {
    return count + ' ' + text;
  }

  return count + ' ' + text + 's';
}

function showGurusInTooltip(event) {
  let gurus;
  const stockSymbol = document.querySelector('.line-chart').dataset.stockSymbol;
  const button = event.target;
  const el = getParent(button, 'activitiy-chart__boxes');

  button.removeEventListener('click', showGurusInTooltip);
  button.classList.add('activitiy-chart__tooltip-open-gurus');

  if (button.dataset.type === 'sell') {
    gurus = JSON.parse(el.dataset.sellGurus);
  } else {
    gurus = JSON.parse(el.dataset.buyGurus);
  }

  gurus.forEach(function (guru, i) {
    const link = document.createElement('a');
    link.href = guru.guru_transaction_path.replace('symbol', stockSymbol);
    if (!guru.index_guru_transactions_page) {
      link.setAttribute('rel', 'nofollow');
    }
    const text = document.createTextNode(guru.name + ' (' + guru.company_name + ')');
    link.appendChild(text);
    button.insertAdjacentElement('afterend', link);
  });
}